import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, CheckCircle2, X, Timer, Plus, MapPin, Repeat, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { loadStoredUser } from "@/lib/api";
import RecurrenceEditor from "@/components/tasks/RecurrenceEditor";

const STATUS_ORDER = { pending: 0, snoozed: 1, delivered: 2, completed: 3, dismissed: 4 };

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function RemindersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [worksites, setWorksites] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, prefs, w] = await Promise.all([
        api.get("/tenant/reminders", { params: { limit: 100 } }),
        api.get("/preferences").catch(() => ({ data: {} })),
        api.get("/time/my-worksites").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(r.data.items || []);
      setPushEnabled(Boolean(prefs.data?.reminders?.web_push));
      setWorksites(w.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const s = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
      if (s !== 0) return s;
      return a.fire_at.localeCompare(b.fire_at);
    });
  }, [items]);

  const doAction = async (r, action, body) => {
    try {
      await api.post(`/tenant/reminders/items/${r.id}/${action}`, body);
      toast.success(`Reminder ${action}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="reminders-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Your work</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Reminders</h1>
          <p className="mt-2 text-muted-foreground">Timely nudges you set for yourself, or that your supervisor set for you.</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="reminder-create-btn">
          <Plus className="mr-1.5 h-4 w-4" /> New reminder
        </Button>
      </div>

      {!pushEnabled && (
        <div className="flex items-center justify-between rounded-xl border border-warning/40 bg-warning/5 px-4 py-3 text-sm">
          <div className="flex items-center gap-2 text-warning">
            <BellRing className="h-4 w-4" />
            Push is off — enable it so reminders reach you off-tab.
          </div>
          <Button asChild size="sm" variant="outline" data-testid="reminders-enable-push-link">
            <Link to="/app/settings/notifications">Enable</Link>
          </Button>
        </div>
      )}

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && sorted.length === 0 && (
        <div className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
          Nothing pending. Create a reminder to nudge yourself.
        </div>
      )}
      <div className="grid gap-2">
        {sorted.map((r) => (
          <Card key={r.id} data-testid={`reminder-card-${r.id}`}>
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                  <Badge variant={r.status === "pending" || r.status === "snoozed" ? "default" : "outline"} className="rounded-full">{r.status}</Badge>
                  {r.recurrence_rrule && <Badge variant="outline" className="rounded-full"><Repeat className="mr-1 h-3 w-3" /> series</Badge>}
                  {r.location_trigger && (
                    <Badge variant="outline" className="rounded-full">
                      <MapPin className="mr-1 h-3 w-3" /> {r.location_trigger.event}
                    </Badge>
                  )}
                </div>
                <div className="mt-1 font-medium text-primary">{r.title}</div>
                {r.body && <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">{r.body}</div>}
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Timer className="h-3 w-3" />
                  {r.status === "snoozed" ? `Snoozed until ${fmt(r.snoozed_until)}` : `Fires ${fmt(r.fire_at)}`}
                </div>
              </div>
              <div className="flex flex-shrink-0 flex-wrap gap-2">
                {(r.status === "pending" || r.status === "snoozed" || r.status === "delivered") && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => doAction(r, "snooze", { minutes: 15 })} data-testid={`reminder-snooze15-${r.id}`}>+15m</Button>
                    <Button size="sm" variant="outline" onClick={() => doAction(r, "snooze", { minutes: 60 })} data-testid={`reminder-snooze60-${r.id}`}>+1h</Button>
                    <Button size="sm" variant="outline" onClick={() => doAction(r, "snooze", { minutes: 60 * 24 })} data-testid={`reminder-snoozeTomorrow-${r.id}`}>Tomorrow</Button>
                    <Button size="sm" onClick={() => doAction(r, "complete", {})} data-testid={`reminder-complete-${r.id}`}>
                      <CheckCircle2 className="mr-1 h-4 w-4" /> Done
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => doAction(r, "dismiss", {})} data-testid={`reminder-dismiss-${r.id}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ReminderDialog
        open={creating}
        onOpenChange={setCreating}
        worksites={worksites}
        onSaved={load}
      />
    </div>
  );
}


function ReminderDialog({ open, onOpenChange, worksites, onSaved, forUserId }) {
  const user = loadStoredUser();
  const targetId = forUserId || user?.id;
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [fireAt, setFireAt] = useState("");
  const [rrule, setRrule] = useState(null);
  const [locEnabled, setLocEnabled] = useState(false);
  const [locWs, setLocWs] = useState("");
  const [locEvent, setLocEvent] = useState("enter");
  const [channels, setChannels] = useState(new Set(["in_app"]));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setBody("");
    const d = new Date();
    d.setMinutes(d.getMinutes() + 30, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    setFireAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setRrule(null);
    setLocEnabled(false);
    setLocWs("");
    setLocEvent("enter");
    setChannels(new Set(["in_app"]));
  }, [open]);

  const toggleChan = (c) => {
    const s = new Set(channels);
    s.has(c) ? s.delete(c) : s.add(c);
    setChannels(s);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body_ = {
        user_id: targetId,
        title,
        body: body || null,
        fire_at: new Date(fireAt).toISOString(),
        scope: "self",
        recurrence_rrule: rrule || null,
        delivery_channels: Array.from(channels),
      };
      if (locEnabled && locWs) {
        body_.location_trigger = { worksite_id: locWs, event: locEvent };
      }
      await api.post("/tenant/reminders", body_);
      toast.success("Reminder set");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle>New reminder</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="rem-title">Title</Label>
            <Input id="rem-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="reminder-title-input" />
          </div>
          <div>
            <Label htmlFor="rem-body">Body (optional)</Label>
            <Textarea id="rem-body" value={body} onChange={(e) => setBody(e.target.value)} rows={2} maxLength={2000} data-testid="reminder-body-input" />
          </div>
          <div>
            <Label htmlFor="rem-fire">Fire at</Label>
            <Input id="rem-fire" type="datetime-local" value={fireAt} onChange={(e) => setFireAt(e.target.value)} data-testid="reminder-fire-input" />
          </div>
          <div>
            <Label>Channels</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                { code: "in_app", label: "In-app (always)" },
                { code: "push", label: "Push" },
                { code: "email", label: "Email" },
              ].map((c) => (
                <label key={c.code} className="flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs">
                  <Checkbox
                    checked={channels.has(c.code)}
                    onCheckedChange={() => toggleChan(c.code)}
                    disabled={c.code === "in_app"}
                    data-testid={`reminder-channel-${c.code}`}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <RecurrenceEditor value={rrule} onChange={setRrule} />
          <div className="rounded-xl border border-border p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Fire when I arrive/leave</div>
                <div className="text-xs text-muted-foreground">Overrides the scheduled time.</div>
              </div>
              <Checkbox
                checked={locEnabled}
                onCheckedChange={setLocEnabled}
                data-testid="reminder-loc-enabled"
              />
            </div>
            {locEnabled && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Select value={locWs || "__none__"} onValueChange={(v) => setLocWs(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="reminder-loc-worksite"><SelectValue placeholder="Worksite" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Choose worksite</SelectItem>
                    {worksites.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} ({w.code})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={locEvent} onValueChange={setLocEvent}>
                  <SelectTrigger data-testid="reminder-loc-event"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enter">On enter</SelectItem>
                    <SelectItem value="exit">On exit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="reminder-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving || !title || !fireAt} data-testid="reminder-save-btn">
            {saving ? "Saving…" : "Create reminder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
